import { useState } from 'react';

import { Security as SecurityIcon } from '@mui/icons-material';
import {
	alpha,
	Alert,
	Box,
	Button,
	Dialog,
	DialogContent,
	Link,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { UsersService } from '~features/users';

import PrivacyPolicyDocumentService from '../services/privacyPolicyDocumentService';
import pickLocalization from '../utils/pickLocalization';

const usersService = new UsersService();
const documentService = new PrivacyPolicyDocumentService();

interface PrivacyPolicyDialogProps {
	/**
	 * Called after the backend has recorded the acceptance, so the caller can
	 * refresh the user it decided to show this dialog for.
	 */
	onAccepted: () => void;
}

/**
 * The blocking dialog asking the user to accept the updated privacy policy.
 *
 * Deliberately built on the MUI dialog instead of the shared `Dialog` wrapper:
 * that wrapper always renders a title slot and its close affordances, and this
 * dialog is icon led and has no way out other than accepting.
 */
const PrivacyPolicyDialog = ({ onAccepted }: PrivacyPolicyDialogProps) => {
	const { t, i18n } = useTranslation();

	const [isAccepting, setIsAccepting] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	// A failing document call must not keep the user from accepting, so the
	// error is swallowed here and only the link disappears.
	const { data: document } = useSWR('privacyPolicyDocument', () =>
		documentService.getPrivacyPolicy().catch(() => null)
	);
	const policyUrl = document ? pickLocalization(document.localizations, i18n.language)?.url : null;

	const handleAccept = async () => {
		if (isAccepting) {
			return;
		}

		setIsAccepting(true);
		setErrorMessage(null);

		try {
			await usersService.acceptPrivacyPolicy();
			onAccepted();
		} catch {
			// A 401 never reaches here as a 401: the shared response interceptor
			// in `TopologyService` claims it first, tries a silent renew and only
			// then rejects, so this branch cannot tell an expired session apart
			// from any other failure. Handling the expired session stays where it
			// already lives - the interceptor and the route guard.
			//
			// The dialog is blocking, so it stays up and says why. A snackbar
			// would render behind the backdrop of the only action on screen.
			setErrorMessage(t('somethingWentWrong'));
			setIsAccepting(false);
		}
	};

	return (
		<Dialog
			open
			disableEscapeKeyDown
			fullWidth
			maxWidth='xs'
			aria-labelledby='privacy-policy-dialog-title'
		>
			<DialogContent>
				<Stack spacing={2} alignItems='center' textAlign='center' py={1}>
					<Box
						display='flex'
						alignItems='center'
						justifyContent='center'
						width={56}
						height={56}
						borderRadius='50%'
						bgcolor={(theme) => alpha(theme.palette.primary.main, 0.08)}
						color={(theme) => theme.palette.primary.main}
					>
						<SecurityIcon fontSize='large' />
					</Box>

					<Typography id='privacy-policy-dialog-title' variant='h4'>
						{t('views.privacyPolicy.dialog.update.title')}
					</Typography>

					<Typography variant='body2' color='text.secondary'>
						{t('views.privacyPolicy.dialog.update.description')}
					</Typography>

					{errorMessage != null && (
						<Alert severity='error' sx={{ width: 1 }}>
							{errorMessage}
						</Alert>
					)}

					<Button
						variant='contained'
						size='large'
						fullWidth
						loading={isAccepting}
						onClick={handleAccept}
					>
						{t('views.privacyPolicy.dialog.update.confirm')}
					</Button>

					{policyUrl != null && (
						<Link
							href={policyUrl}
							target='_blank'
							rel='noopener noreferrer'
							variant='body2'
							underline='hover'
						>
							{t('views.privacyPolicy.dialog.update.readFullPolicy')}
						</Link>
					)}

					<Typography variant='caption' color='text.secondary'>
						{t('views.privacyPolicy.dialog.update.disclaimer')}
					</Typography>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default PrivacyPolicyDialog;
